import { useForm } from "antd/lib/form/Form";
import React from "react";
import { useEffect } from "react";
import { useState } from "react";
import TextMessagePageForm from "./TextMessagePageForm";
import { sendTextMessage } from "../../api/TextMessageApi";
import { adnNotification } from "../common/adnNotification/adnNotification";
import { message } from "antd";
import { getEmployees } from "../../api/EmployeeApi";
import { getUsers } from "../../api/UserApi";
import { appTitle } from "../common/GeneralConfigurations";
import { getStaff } from "../../api/OrganizationStaffApi";
import { AppRoutes } from "../../AppRoutes";
import { useSearchParams } from "react-router-dom";
import { getMessageStatisticById } from "../../api/MessageStatisticApi";

const ManageTextMessagePage = () => {
  const [searchParams] = useSearchParams();
  const statisticId = searchParams.get("repeat");
  const [form] = useForm();
  const [rememberMessage, setRememberMessage] = useState(false);
  const [numberOfMessages, setNumberOfMessages] = useState(0);
  const [showButton, setShowButton] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const [departmentOptions, setDepartmentOptions] = useState([
    {
      id: 1,
      name: "Consultants",
      fetch: getEmployees,
      entityRoute: AppRoutes.consultantById,
    },
    {
      id: 2,
      name: "Manager",
      fetch: (currentPage, pageSize, orderBy, searchName) =>
        getUsers(currentPage, pageSize, orderBy, searchName, "", "Manager"),
      entityRoute: AppRoutes.userById,
    },
    {
      id: 3,
      name: "Human Resources",
      fetch: (currentPage, pageSize, orderBy, searchName) =>
        getUsers(
          currentPage,
          pageSize,
          orderBy,
          searchName,
          "",
          "Human Resources"
        ),
      entityRoute: AppRoutes.userById,
    },
    {
      id: 4,
      name: "Staff",
      fetch: (currentPage, pageSize, orderBy, searchName) =>
        getStaff(currentPage, pageSize, orderBy, searchName, true),
      entityRoute: AppRoutes.organizationStaffById,
    },
  ]);

  const [allSelected, setAllSelected] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [addPhoneNumberError, setAddPhoneNumberError] = useState("");
  const [requiresFetch, setRequiresFetch] = useState(false);

  useEffect(() => {
    document.title = `${appTitle} - Send Message`;
  }, []);

  // Fill with previous information if redirected from history
  useEffect(async () => {
    if (statisticId) {
      const res = await getMessageStatisticById(statisticId);
      if (res.status == 200) {
        form.setFieldsValue({
          campaign: res.data.campaign,
          body: res.data.body,
        });
      }
    }
  }, []);

  // Update number of messages count and check button validity
  useEffect(() => {
    let count = 0;

    for (const contact of selectedContacts) {
      if (contact.type == "Department") {
        count += contact.count;
      } else count++;
    }

    setNumberOfMessages(count);

    onValuesChange(null, form.getFieldsValue(true));
  }, [selectedContacts]);

  const onClickAllSelected = (checked) => {
    setAllSelected(checked);

    if (!checked)
      setSelectedContacts(
        selectedContacts.filter((contact) => contact.type == "Phone")
      );
    else
      setSelectedContacts(
        selectedContacts.filter(
          (contact) => contact.type == "Department" || contact.type == "Phone"
        )
      );
  };

  const addPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) {
      setAddPhoneNumberError("Please input a phone number!");
      return false;
    }

    const numberExists = selectedContacts.find(
      (contact) => contact.phone == phoneNumber
    );
    if (numberExists) {
      if (numberExists.type == "Phone") {
        setAddPhoneNumberError(
          "That number as already been added to the list."
        );
      } else {
        setAddPhoneNumberError(`That number belongs to ${numberExists.name}.`);
      }
      return false;
    }

    setSelectedContacts([
      ...selectedContacts,
      { type: "Phone", phone: phoneNumber },
    ]);
    setAddPhoneNumberError("");
    return true;
  };

  const onRemoveContact = (contact) => {
    setSelectedContacts(
      selectedContacts.filter(
        (c) =>
          contact.id != c.id ||
          contact.type != c.type ||
          contact.phone != c.phone
      )
    );
  };

  const onValuesChange = (_, values) => {
    const hasCampaign = values.campaign && values.campaign != "";
    const hasBody = values.body && values.body != "";
    const hasContacts = selectedContacts.length > 0 || allSelected;

    setShowButton(hasCampaign && hasBody && hasContacts);
  };

  // Hand send button (submit button)
  const handleSend = async (values) => {
    setSubmitLoading(true);

    let requestBody = {
      campaign: values.campaign,
      body: values.body,
      phoneNumbers: [],
      departmentIds: [],
      employeeIds: [],
      managerIds: [],
      humanResourcesIds: [],
      staffIds: [],
    };

    for (const contact of selectedContacts) {
      switch (contact.type) {
        case "Phone":
          requestBody.phoneNumbers.push(contact.phone);
          break;
        case "Consultants":
          requestBody.employeeIds.push(contact.id);
          break;
        case "Manager":
          requestBody.managerIds.push(contact.id);
          break;
        case "Human Resources":
          requestBody.humanResourcesIds.push(contact.id);
          break;
        case "Staff":
          requestBody.staffIds.push(contact.id);
          break;
        case "Department":
          requestBody.departmentIds.push(contact.id);
          break;
      }
    }

    await send(requestBody);

    setSubmitLoading(false);
  };

  // Send to phone numbers (one or more)
  const send = async (body) => {
    const res = await sendTextMessage(body);
    if (res.status === 201) {
      message.success("Sent text message.");

      let invalidNumbers = false;
      for (const contact of res.data.contacts) {
        if (!contact.sent) invalidNumbers = true;
      }

      if (invalidNumbers) {
        setRequiresFetch(!requiresFetch);
        adnNotification(
          "Error:",
          "Problem with one or more contacts, message wasn't sent to them. These are marked and must be checked."
        );
      }

      if (!rememberMessage) {
        form.setFieldsValue({ campaign: "", body: "" });
      }
      setAllSelected(false);
      setSelectedContacts([]);
    } else {
      if (
        res.data.errors.find(
          (error) => error.message == "Phone Numbers is invalid"
        )
      ) {
        adnNotification(
          "Error:",
          "One of the unregistered numbers is invalid. Please check all."
        );
      }

      message.error("Failed to send text message.");
    }
  };

  return (
    <TextMessagePageForm
      form={form}
      departmentOptions={departmentOptions}
      onValuesChange={onValuesChange}
      rememberMessage={rememberMessage}
      onChangeRememberMessage={(e) => setRememberMessage(e.target.checked)}
      allSelected={allSelected}
      setAllSelected={setAllSelected}
      onClickAllSelected={onClickAllSelected}
      selectedContacts={selectedContacts}
      setSelectedContacts={setSelectedContacts}
      requiresFetch={requiresFetch}
      onRemoveContact={onRemoveContact}
      addPhoneNumber={addPhoneNumber}
      addPhoneNumberError={addPhoneNumberError}
      showButton={showButton}
      numberOfMessages={numberOfMessages}
      submitLoading={submitLoading}
      handleSend={handleSend}
    />
  );
};

export default ManageTextMessagePage;
